import React, {FC, useEffect, useState} from 'react';
import {AddressPopUp} from '../AddressPopUp';
import {Button} from '../Button';
import {H6} from '../H6';
import {PhoneNumber} from '../PhoneNumber';
import {Section} from '../Section';
import {TextField} from '../TextField';
import {InfoItem} from './InfoItem';
import {getUserAddresses} from "../../apis/user";
import {OrderChangeAddressDto, TOrderDetail, UserAddress} from "../../types/types";
import {useForm} from "react-hook-form";
import {PostPopUp} from "../PostPopUp";
import {Checkbox} from "../Checkbox";
import { orderChangeAvailable } from '../../apis/orders';
import { nToast } from '../../plugins/reactToast';
import { useUserState } from '../../context/UserContext';
import {addressSort} from "../../utils/addressSort";
import {ChangeAddressPopup} from "../ChangeAddressPopup";

interface ShippingInfoSectionProps {
    hideActions?: boolean
    order: TOrderDetail
    changed?: OrderChangeAddressDto
    onChangeAddress?: (data: OrderChangeAddressDto) => void
    isExchange?: boolean
}

export const ShippingInfoSection: FC<ShippingInfoSectionProps> = (props) => {
    const {
        hideActions = false,
        order,
        onChangeAddress,
        changed,
        isExchange = false,
    } = props;
    const [toggle, setToggle] = useState(false);
    const [addressPopUp, setAddressPopUp] = useState(false);
    const [isNewAddress, setIsNewAddress] = useState<boolean>(false);
    const [addresses, setAddresses] = useState<UserAddress[]>([]);
    const [postModal, setPostModal] = useState<boolean>(false);
    const {isLoggedIn} = useUserState();
    const form = useForm<OrderChangeAddressDto>();
    const [changeAddressPopupOpen, setChangeAddressPopupOpen] = useState<boolean>(false);

    useEffect(() => {
        isLoggedIn && getUserAddresses().then(r => {
            const adds = addressSort(r.data.payload);
            setAddresses(adds);
        });
    }, [isLoggedIn])

    useEffect(() => {
        form.setValue(`newUserAddress.addressName`, order.orderRequestExtra?.addressName || order.addressName);
        form.setValue(`userAddressId`, order.orderRequestExtra?.userAddressId || order.userAddressId);
        form.setValue(`receiverName`, order.orderRequestExtra?.receiverName || order.receiverName!);
        form.setValue(`receiverPhone`, order.orderRequestExtra?.receiverPhone || order.receiverPhone);
        form.setValue(`receiverPostcode`, order.orderRequestExtra?.receiverPostcode || order.receiverPostcode);
        form.setValue(`receiverAddress`, order.orderRequestExtra?.receiverAddress || order.receiverAddress);
        form.setValue(`receiverDetailAddress`, order.orderRequestExtra?.receiverDetailAddress || order.receiverDetailAddress);
        form.setValue(`shippingRequest`, order.orderRequestExtra?.shippingRequest || order.shippingRequest);
    }, [order])

    const onSave = () => {
        form.setValue(`customerName`, order.customerName);
        form.setValue(`customerPhone`, order.customerPhone);
        if (isNewAddress) {
            form.resetField('userAddressId')
            form.setValue('newUserAddress.receiverName', form.watch().receiverName)
            form.setValue('newUserAddress.phone', form.watch().receiverPhone)
            form.setValue('newUserAddress.detailAddress', form.watch().receiverDetailAddress || '')
            form.setValue('newUserAddress.requestMessage', form.watch().shippingRequest)
        } else {
            form.resetField('newUserAddress')
        }
        onChangeAddress && onChangeAddress(form.watch());
        setToggle(false);
    }

    const checkAndToggle = () => {
        orderChangeAvailable(order.orderRequestExtra?.requestOrderNumber || order.orderNumber).then(() => setToggle(true)).catch(() => {
            nToast(isExchange ? '택배 회수 접수가 완료되어 회수지 변경이 불가능합니다.' : '이미 배송준비중이라 변경이 불가합니다.');
            setToggle(false);
        });
    }

    return (
        <>
            <ChangeAddressPopup open={changeAddressPopupOpen} onClose={() => setChangeAddressPopupOpen(false)}
                                onOK={() => {
                                    onSave();
                                    setChangeAddressPopupOpen(false);
                                }}
            />
            <PostPopUp open={postModal} onClose={() => setPostModal(false)}
                       onSelected={data => {
                           if (isNewAddress) {
                               form.setValue('newUserAddress.postcode', data.zonecode.toString())
                               form.setValue('newUserAddress.sido', data.sido)
                               form.setValue('newUserAddress.gugun', data.sigungu)
                               form.setValue('newUserAddress.address', data.address.replace(data.sido, '').replace(data.sigungu, '').trim())
                           }
                           form.setValue('receiverPostcode', data.zonecode.toString())
                           form.setValue('receiverAddress', data.address)
                           setPostModal(false);
                       }}
            />
            <AddressPopUp open={addressPopUp}
                          onClose={() => setAddressPopUp(false)}
                          addresses={addresses}
                          onSelected={(data) => {
                              form.resetField('newUserAddress')
                              form.setValue('newUserAddress.addressName', data.addressName)
                              form.setValue('userAddressId', data.addressId)
                              form.setValue('receiverName', data.receiverName!)
                              form.setValue('receiverPhone', data.phone)
                              form.setValue('receiverPostcode', data.postcode)
                              form.setValue('receiverAddress', [data.sido, data.gugun, data.address].filter(Boolean).join(' '))
                              form.setValue('receiverDetailAddress', data.detailAddress)
                              form.setValue('shippingRequest', data.requestMessage)
                              setAddressPopUp(false);
                          }}
            />

            {toggle ? (
                <Section className="space-y-4">
                    <H6 className="border-b pb-2">배송 정보</H6>
                    <div className="flex">
                        <div className="space-x-4">
                            <Button
                                text="기존 배송지"
                                className={isNewAddress ? "bg-brand-2 text-gray-900 px-8" : "filled-brand-1 px-8"}
                                onClick={() => {
                                    setIsNewAddress(false);
                                    setAddressPopUp(true);
                                }}/>
                            {addresses.length < 5 && (
                                <Button
                                    text="신규 입력"
                                    className={isNewAddress ? "filled-brand-1 px-8" : "bg-brand-2 text-gray-900 px-8"}
                                    onClick={() => {
                                        setIsNewAddress(true);
                                        form.setValue(`newUserAddress.addressName`, '');
                                        form.setValue(`receiverName`, '');
                                        form.setValue(`receiverPhone`, '');
                                        form.setValue(`receiverPostcode`, '');
                                        form.setValue(`receiverAddress`, '');
                                        form.setValue(`receiverDetailAddress`, '');
                                        form.setValue(`shippingRequest`, '');
                                    }}/>
                            )}

                        </div>
                        <div className="flex-1"/>
                        <button
                            onClick={() => setAddressPopUp(true)}
                            className="text-13 underline"
                        >
                            배송지 목록
                        </button>
                    </div>

                    <div className="flex flex-col md:grid md:grid-cols-2 md:w-2/3 gap-4">
                        <TextField
                            label="배송지명 *"
                            placeholder="배송지명을 입력해주세요."
                            disabled={!isNewAddress}
                            value={form.watch().newUserAddress?.addressName}
                            {...form.register(`newUserAddress.addressName`, {required: true})}
                        />
                        <TextField
                            label="수령인 이름 *"
                            placeholder="수령인 이름을 적어주세요."
                            {...form.register(`receiverName`, {required: true})}
                        />
                        <PhoneNumber label="수취인 전화번호 *"
                                     value={form.watch().receiverPhone}
                                     onChange={(e) => form.setValue(`receiverPhone`, e.target.value)}
                        />
                        <div/>

                        <div className="col-span-2 space-y-2">
                            <div className="flex items-end space-x-4">
                                <div className="flex-1">
                                    <TextField label="기본 주소 *" placeholder="주소" disabled={true}
                                               value={`${form.watch().receiverPostcode || ''} ${form.watch().receiverAddress || ''}`}
                                    />
                                </div>
                                <Button text="검색"
                                        className="filled-gray-200 text-black font-light text-sm md:px-10"
                                        onClick={() => setPostModal(true)}
                                />
                            </div>
                            <TextField placeholder="상세 주소 입력"
                                       {...form.register(`receiverDetailAddress`, {required: true})}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextField
                                label="배송시 요청사항"
                                placeholder="배송시 요청사항을 입력해주세요."
                                {...form.register(`shippingRequest`)}
                            />
                        </div>
                        {isNewAddress && (
                            <Checkbox label="기본 배송지로 등록"
                                      checked={form.watch().newUserAddress?.isDefault}
                                      onChange={() => form.setValue(`newUserAddress.isDefault`, !form.watch().newUserAddress?.isDefault)}
                            />
                        )}
                    </div>

                    <div className="flex flex-row-reverse space-x-reverse space-x-4">
                        <Button
                            onClick={() => setChangeAddressPopupOpen(true)}
                            text="저장"
                            className="filled-gray-900 px-12"
                        />
                        <Button
                            onClick={() => setToggle(false)}
                            text="취소"
                            className="bg-gray-100 px-12"
                        />
                    </div>
                </Section>
            ) : (
                changed ? (
                    <Section className="space-y-4 text-13">
                        <div className="flex justify-between items-center border-b pb-2">
                            <H6>배송 정보</H6>
                            {!hideActions && (
                                <div className={'space-y-4 text-right'}>
                                    <button
                                        onClick={checkAndToggle}
                                        className="text-12 bg-gray-100 px-3 py-2 rounded-md"
                                    >
                                        {isExchange ? '배송정보 변경' : '배송지 변경'}
                                    </button>
                                    <p className={'text-12 text-gray-500'}>※ 배송정보 변경 시 회수지와 수령지 모두 변경됩니다.</p>
                                </div>
                            )}
                        </div>
                        <div className="grid md:grid-cols-2 gap-y-2">
                            <InfoItem title="배송지명" content={changed.newUserAddress?.addressName!}/>
                            <div/>
                            <InfoItem title="수취인 이름" content={changed.receiverName!}/>
                            <InfoItem title="수취인 전화번호" content={changed.receiverPhone!}/>
                            <InfoItem
                                title="주소"
                                content={`${changed.receiverAddress} ${changed.receiverDetailAddress}`}
                            />
                            <InfoItem title="배송지 요청사항" content={changed.shippingRequest!}/>
                        </div>
                    </Section>
                ) : (
                    <Section className="space-y-4 text-13">
                        <div className="flex justify-between items-center border-b pb-2">
                            <H6>배송 정보</H6>
                            {!hideActions && (
                                <div className={'space-y-4 text-right'}>
                                    <button
                                        onClick={checkAndToggle}
                                        className="text-12 bg-gray-100 px-3 py-2 rounded-md"
                                    >
                                        {isExchange ? '배송정보 변경' : '배송지 변경'}
                                    </button>
                                    <p className={'text-12 text-gray-500'}>※ 배송정보 변경 시 회수지와 수령지 모두 변경됩니다.</p>
                                </div>
                            )}
                        </div>
                        <div className="grid md:grid-cols-2 gap-y-2">
                            <InfoItem title="배송지명" content={form.watch('newUserAddress.addressName')}/>
                            <div/>
                            <InfoItem title="수취인 이름" content={form.watch('receiverName')}/>
                            <InfoItem title="수취인 전화번호" content={form.watch('receiverPhone')}/>
                            <InfoItem
                                title="주소"
                                content={`${form.watch('receiverAddress')} ${form.watch('receiverDetailAddress')}`}
                            />
                            <InfoItem title="배송지 요청사항" content={form.watch('shippingRequest') || '-'}/>
                        </div>
                    </Section>
                )
            )}
        </>
    );
};
